import { Button } from '@sortlist-frontend/design-system';
import { Trash2 } from '@sortlist-frontend/design-system/icons';
import { formatNumber } from '@sortlist-frontend/utils';
import { useState } from 'react';

import type { GuideCalculatorRow, GuideCalculatorVerdict, GuideLabels } from '../types';

interface CalculatorRow extends GuideCalculatorRow {
  id: number;
}

interface ExecutionCalculatorProps {
  rows: readonly GuideCalculatorRow[];
  types: readonly { id: string; label: string }[];
  verdicts: readonly GuideCalculatorVerdict[];
  ticks: readonly string[];
  addLabel: string;
  totalLabel: string;
  footnote: string;
  locale: string;
  labels: Pick<GuideLabels, 'executions'>;
}

const DAYS = 30;

const monthlyRuns = (type: string, value: number) => {
  if (type === 'interval') return value > 0 ? (24 * 60 * DAYS) / value : 0;
  return value * DAYS;
};

const position = (total: number) => Math.max(0, Math.min(1, (Math.log10(Math.max(total, 100)) - 2) / 3));

export const ExecutionCalculator = (props: ExecutionCalculatorProps) => {
  const { rows: initialRows, types, verdicts, ticks, addLabel, totalLabel, footnote, locale, labels } = props;
  const [rows, setRows] = useState<readonly CalculatorRow[]>(initialRows.map((row, id) => ({ ...row, id })));
  const [nextId, setNextId] = useState(initialRows.length);
  const perRow = rows.map((row) => Math.round(monthlyRuns(row.type, row.value)));
  const total = perRow.reduce((sum, value) => sum + value, 0);
  const verdict = verdicts.find((entry) => entry.upTo == null || total <= entry.upTo) ?? verdicts[verdicts.length - 1];
  const update = (index: number, patch: Partial<GuideCalculatorRow>) =>
    setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const [totalBefore, totalAfter] = totalLabel.split('{total}');

  return (
    <div className="rounded-2xl border border-primary-200 bg-primary-50 p-4 md:p-6">
      <ul className="space-y-3">
        {rows.map((row, index) => (
          <li
            key={`${index}-${row.name}`}
            className="grid items-end gap-2 rounded-xl bg-white p-3 sm:grid-cols-[1.4fr_1fr_6rem_6rem_auto]">
            <label className="text-xs font-semibold text-secondary-700">
              Workflow
              <input
                type="text"
                value={row.name}
                onChange={(event) => update(index, { name: event.target.value })}
                className="mt-1 w-full rounded-md border border-secondary-200 px-2 py-1.5 text-sm font-normal text-secondary-950"
              />
            </label>
            <label className="text-xs font-semibold text-secondary-700">
              Type
              <select
                value={row.type}
                onChange={(event) => update(index, { type: event.target.value })}
                className="mt-1 w-full rounded-md border border-secondary-200 bg-white px-2 py-1.5 text-sm font-normal text-secondary-950">
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold text-secondary-700">
              N
              <input
                type="number"
                min={0}
                value={row.value}
                onChange={(event) => update(index, { value: Math.max(0, Number(event.target.value) || 0) })}
                className="mt-1 w-full rounded-md border border-secondary-200 px-2 py-1.5 text-sm font-normal text-secondary-950"
              />
            </label>
            <p className="text-sm text-secondary-700">
              <strong className="text-secondary-950">{formatNumber({ value: perRow[index], locale })}</strong>{' '}
              {labels.executions}
            </p>
            <button
              type="button"
              onClick={() => setRows(rows.filter((_, i) => i !== index))}
              aria-label={`${row.name} ×`}
              className="justify-self-end rounded-md p-2 text-secondary-500 hover:bg-secondary-100 hover:text-danger-500">
              <Trash2 size={16} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          color="primary"
          variant="bordered"
          size="md"
          onPress={() => {
            setRows([...rows, { id: nextId, name: '', type: types[0].id, value: 1 }]);
            setNextId(nextId + 1);
          }}>
          {addLabel}
        </Button>
        <p className="text-sm text-secondary-500" aria-live="polite">
          {totalBefore}
          <strong className="text-lg text-secondary-950">{formatNumber({ value: total, locale })}</strong>
          {totalAfter}
        </p>
      </div>
      <div className="relative mb-10 mt-12">
        <progress
          value={Math.round(position(total) * 100)}
          max={100}
          aria-label={totalLabel.replace('{total}', formatNumber({ value: total, locale }))}
          className="h-3 w-full appearance-none overflow-hidden rounded-full bg-secondary-100 [&::-moz-progress-bar]:bg-primary-500 [&::-webkit-progress-bar]:bg-secondary-100 [&::-webkit-progress-value]:bg-primary-500"
        />
        <ul className="mt-2 flex justify-between text-xs text-secondary-500">
          {ticks.map((tick) => (
            <li key={tick}>{tick}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl bg-white p-4" aria-live="polite">
        <p className="text-lg font-bold text-secondary-950">{verdict.title}</p>
        <p className="mt-1 text-sm text-secondary-700">{verdict.text}</p>
      </div>
      <p className="mt-3 text-xs text-secondary-500">{footnote}</p>
    </div>
  );
};
